import {
    ArrowPathIcon,
    BanknotesIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    PhoneIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const SmsTestPanel = ({ onSmsSent }) => {
  const [loading, setLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingData, setBillingData] = useState([]);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [selectedBank, setSelectedBank] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  
  // 은행 목록
  const banks = [
    '국민은행', '신한은행', '우리은행', '하나은행', 
    '농협은행', '기업은행', '카카오뱅크', '토스뱅크'
  ];
  
  // 은행별 SMS 템플릿
  const bankTemplates = {
    '국민은행': '[국민은행] {date} {time} {sender}님께 {amount}원이 입금되었습니다. 잔액: {balance}원',
    '신한은행': '[신한] {date} {time} {sender} → {account} {amount}원 입금 잔액: {balance}원',
    '우리은행': '[우리] {date} {time} {sender}님께 {amount}원이 입금되었습니다. 잔액: {balance}원',
    '하나은행': '[하나] {date} {time} {sender}님께 {amount}원이 입금되었습니다. 잔액: {balance}원',
    '농협은행': '[농협] {date} {time} {sender}님께 {amount}원이 입금되었습니다. 잔액: {balance}원',
    '기업은행': '[기업] {date} {time} {sender}님께 {amount}원이 입금되었습니다. 잔액: {balance}원',
    '카카오뱅크': '[카카오뱅크] {date} {time} {sender}님께 {amount}원이 입금되었습니다. 잔액: {balance}원',
    '토스뱅크': '[토스뱅크] {date} {time} {sender}님께 {amount}원이 입금되었습니다. 잔액: {balance}원'
  };

  // 샘플 데이터 생성 함수
  const getSampleData = () => [
    {
      id: 1,
      tenant_name: '김철수',
      tenant_phone: '010-1234-5678',
      building_name: '아파트A',
      unit_number: '101호',
      total_amount: 500000,
      billing_month: '2024-01'
    },
    {
      id: 2,
      tenant_name: '이영희',
      tenant_phone: '010-2345-6789',
      building_name: '아파트A',
      unit_number: '102호',
      total_amount: 450000,
      billing_month: '2024-01'
    },
    {
      id: 3,
      tenant_name: '박민수',
      tenant_phone: '010-3456-7890',
      building_name: '아파트B',
      unit_number: '201호',
      total_amount: 600000,
      billing_month: '2024-01'
    }
  ];

  // 컴포넌트 마운트 시 청구서 데이터 로드
  useEffect(() => {
    loadBillingData();
  }, []);

  // 청구서 데이터 로드
  const loadBillingData = async () => {
    setBillingLoading(true);
    try {
      console.log('청구서 데이터 로드 시작...');
      
      // 먼저 대시보드 API로 시도
      let response;
      try {
        response = await api.get('/payment/dashboard');
        console.log('대시보드 API 응답:', response.data);
        
        if (response.data && response.data.data && response.data.data.recent_billing_statements) {
          const billingData = response.data.data.recent_billing_statements.map(stmt => ({
            id: stmt.id,
            tenant_name: stmt.tenant_name,
            tenant_phone: stmt.tenant_phone || '010-0000-0000', // 기본값
            building_name: stmt.unit_info?.split(' ')[0] || '건물명',
            unit_number: stmt.unit_info?.split(' ')[1] || '호',
            total_amount: stmt.total_amount,
            billing_month: stmt.billing_month
          }));
          console.log('대시보드에서 변환된 청구서 데이터:', billingData);
          setBillingData(billingData);
          return;
        }
      } catch (dashboardError) {
        console.log('대시보드 API 실패, billing-payment-summary 시도:', dashboardError);
      }
      
      // 대시보드 실패 시 billing-payment-summary 시도
      response = await api.get('/payment/billing-payment-summary?per_page=20');
      console.log('billing-payment-summary API 응답:', response.data);
      
      if (response.data) {
        // 응답이 배열인 경우와 객체인 경우 모두 처리
        let billingData = [];
        
        if (Array.isArray(response.data)) {
          // 응답이 배열인 경우
          billingData = response.data;
          console.log('응답이 배열입니다:', billingData.length, '개 항목');
        } else if (response.data.data) {
          // 응답이 객체인 경우
          billingData = response.data.data.summary_data || response.data.data || [];
          console.log('응답이 객체입니다:', billingData.length, '개 항목');
        }
        
        if (billingData.length > 0) {
          console.log('설정할 청구서 데이터:', billingData);
          console.log('첫 번째 청구서 구조:', billingData[0]);
          setBillingData(billingData);
        } else {
          console.log('청구서 데이터가 비어있어 샘플 데이터를 사용합니다.');
          setBillingData(getSampleData());
        }
      } else {
        console.log('응답 데이터가 없습니다:', response.data);
        console.log('실제 데이터가 없어 샘플 데이터를 사용합니다.');
        setBillingData(getSampleData());
      }
    } catch (error) {
      console.error('청구서 데이터 로드 실패:', error);
      console.error('에러 상세:', error.response?.data);
      
      // API 실패 시 샘플 데이터 사용
      console.log('API 실패로 인해 샘플 데이터를 사용합니다.');
      setBillingData(getSampleData());
    } finally {
      setBillingLoading(false);
    }
  };

  // SMS 메시지 생성
  const generateSmsMessage = (bank, billing) => {
    if (!bank || !billing) return '';
    
    const now = new Date();
    const date = format(now, 'MM/dd', { locale: ko });
    const time = format(now, 'HH:mm', { locale: ko });
    const balance = Math.floor(Math.random() * 9000000) + 1000000; // 100만~1000만원 랜덤
    
    // 숫자 포맷팅 함수
    const formatNumber = (num) => {
      if (typeof num !== 'number') return '0';
      return num.toLocaleString();
    };
    
    let template = bankTemplates[bank];
    
    if (bank === '신한은행' && template.includes('{account}')) {
      const account = `${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 90) + 10}-${Math.floor(Math.random() * 900000) + 100000}`;
      return template
        .replace('{date}', date)
        .replace('{time}', time)
        .replace('{sender}', billing.tenant_name || '미확인')
        .replace('{account}', account)
        .replace('{amount}', formatNumber(billing.total_amount))
        .replace('{balance}', formatNumber(balance));
    } else {
      return template
        .replace('{date}', date)
        .replace('{time}', time)
        .replace('{sender}', billing.tenant_name || '미확인')
        .replace('{amount}', formatNumber(billing.total_amount))
        .replace('{balance}', formatNumber(balance));
    }
  };

  // 입금처리 함수
  const processPayment = async (matchResult) => {
    if (!matchResult || !matchResult.billing_statement_id) {
      alert('입금처리할 청구서 정보가 없습니다.');
      return;
    }

    setLoading(true);
    try {
      console.log('입금처리 요청 시작:', {
        statement_id: matchResult.billing_statement_id,
        action: 'process'
      });

      const response = await api.post(`/payment/billing-statements/${matchResult.billing_statement_id}/payment-actions`, {
        action: 'process',
        payment_method: 'sms',
        payment_date: new Date().toISOString().split('T')[0],
        notes: 'SMS 자동 입금처리'
      });

      console.log('입금처리 응답 전체:', response);
      console.log('입금처리 응답 데이터:', response.data);
      console.log('입금처리 응답 상태:', response.status);

      if (response.data && response.data.success) {
        setTestResult(prev => ({
          ...prev,
          message: 'SMS 수신, 자동 매칭, 입금처리 모두 완료!',
          showPaymentButton: false
        }));
        
        // 부모 컴포넌트에 데이터 새로고침 알림
        if (onSmsSent) {
          onSmsSent();
        }
      } else {
        console.error('입금처리 실패:', response.data);
        alert('입금처리에 실패했습니다: ' + (response.data?.error || response.data?.message || '알 수 없는 오류'));
      }
    } catch (error) {
      console.error('입금처리 오류:', error);
      console.error('입금처리 오류 응답:', error.response?.data);
      console.error('입금처리 오류 상태:', error.response?.status);
      console.error('입금처리 오류 헤더:', error.response?.headers);
      console.error('입금처리 오류 전체:', error.response);
      
      // 더 자세한 오류 정보 표시
      let errorMessage = '입금처리 중 오류가 발생했습니다.';
      if (error.response) {
        errorMessage += `\n상태: ${error.response.status}`;
        if (error.response.data) {
          errorMessage += `\n오류: ${JSON.stringify(error.response.data)}`;
        }
      } else if (error.request) {
        errorMessage += '\n네트워크 오류: 서버에 연결할 수 없습니다.';
      } else {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // SMS 전송
  const sendSms = async () => {
    if (!selectedBilling || !selectedBank) {
      alert('청구서와 은행을 선택해주세요.');
      return;
    }

    setLoading(true);
    setTestResult(null);

    try {
      const message = customMessage || generateSmsMessage(selectedBank, selectedBilling);
      console.log('생성된 SMS 메시지:', message);
      
      const payload = {
        message: message,
        phone_number: selectedBilling.tenant_phone,
        received_at: new Date().toISOString()
      };

      console.log('SMS 전송 데이터:', payload);
      console.log('SMS 메시지 인코딩 확인:', {
        original: message,
        length: message.length,
        charCodes: message.split('').map(c => c.charCodeAt(0))
      });

      const response = await api.post('/payment/sms/receive', payload);
      console.log('SMS 전송 응답 전체:', response);
      console.log('SMS 전송 응답 데이터:', response.data);
      console.log('응답 데이터 타입:', typeof response.data);
      console.log('응답 데이터 키들:', Object.keys(response.data || {}));
      console.log('응답 데이터 상세:', JSON.stringify(response.data, null, 2));
      console.log('🔍 success 값 확인:', response.data?.success);
      console.log('🔍 success 타입:', typeof response.data?.success);
      console.log('🔍 success === true:', response.data?.success === true);
      
      // 실제 SMS 처리 결과에 따른 응답 처리
      // 백엔드 응답 구조: { data: { match_result: {...}, parsed_data: {...} } }
      const responseData = response.data;
      const matchResult = responseData?.match_result;
      const parsedData = responseData?.parsed_data;
      
      // SMS 파싱이 성공했으면 성공으로 처리
      if (parsedData && parsedData.parsed_successfully) {
        let resultMessage = 'SMS 수신 성공!';
        let showPaymentButton = false;
        
        if (matchResult) {
          if (matchResult.status === 'auto_matched') {
            resultMessage = `SMS 자동 매칭 성공! (점수: ${matchResult.score || 'N/A'}점)`;
            showPaymentButton = true;
          } else if (matchResult.status === 'manual_review') {
            resultMessage = 'SMS 수신 완료. 수동 확인이 필요합니다.';
          } else if (matchResult.status === 'parse_failed') {
            resultMessage = 'SMS 수신 완료. 파싱 실패로 미처리 SMS로 분류되었습니다.';
          } else if (matchResult.status === 'no_match') {
            resultMessage = 'SMS 수신 완료. 매칭된 청구데이터가 없습니다.';
          } else {
            resultMessage = 'SMS 수신 완료. 매칭 실패.';
          }
        }
        
        setTestResult({
          success: true,
          message: resultMessage,
          data: responseData,
          showPaymentButton: showPaymentButton,
          matchResult: matchResult
        });
        
        // 부모 컴포넌트에 SMS 전송 완료 알림
        if (onSmsSent) {
          onSmsSent();
        }
      } else {
        // 파싱 실패 또는 오류
        setTestResult({
          success: false,
          message: 'SMS 전송 실패',
          error: responseData?.error || responseData?.message || '알 수 없는 오류'
        });
      }
    } catch (error) {
      console.error('SMS 전송 오류:', error);
      console.error('에러 상세:', error.response?.data);
      setTestResult({
        success: false,
        message: 'SMS 전송 오류',
        error: error.response?.data?.error || error.response?.data?.message || error.message || '네트워크 오류'
      });
    } finally {
      setLoading(false);
    }
  };

  // 청구서 선택 시 SMS 메시지 자동 생성
  useEffect(() => {
    console.log('selectedBilling 변경됨:', selectedBilling);
    console.log('selectedBank 변경됨:', selectedBank);
    if (selectedBilling && selectedBank) {
      const message = generateSmsMessage(selectedBank, selectedBilling);
      console.log('생성된 SMS 메시지:', message);
      setCustomMessage(message);
    }
  }, [selectedBilling, selectedBank]);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <PhoneIcon className="h-5 w-5 mr-2 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">
              SMS 입금 테스트
            </h3>
          </div>
          <button
            onClick={loadBillingData}
            disabled={billingLoading}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 disabled:opacity-50"
            title="청구서 데이터 새로고침"
          >
            <ArrowPathIcon className={`h-4 w-4 ${billingLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 청구서 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              청구서 선택
            </label>
            <select
              value={selectedBilling ? billingData.findIndex(b => b.id === selectedBilling.id) : ''}
              onChange={(e) => {
                console.log('청구서 선택 이벤트:', e.target.value);
                console.log('현재 청구서 데이터:', billingData);
                
                const selectedIndex = parseInt(e.target.value);
                const billing = billingData[selectedIndex];
                
                console.log('선택된 청구서:', billing);
                setSelectedBilling(billing);
              }}
              disabled={billingLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">
                {billingLoading ? '청구서 데이터를 불러오는 중...' : 
                 billingData.length === 0 ? '청구서 데이터가 없습니다' : '청구서를 선택하세요'}
              </option>
              {billingData.map((billing, index) => (
                <option key={billing.id || `billing-${index}`} value={index}>
                  {billing.tenant_name} - {billing.building_name} {billing.unit_number}호 ({billing.total_amount?.toLocaleString()}원)
                </option>
              ))}
            </select>
          </div>

          {/* 은행 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              은행 선택
            </label>
            <select
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">은행을 선택하세요</option>
              {banks.map((bank) => (
                <option key={bank} value={bank}>
                  {bank}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 선택된 청구서 정보 */}
        {selectedBilling && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              선택된 청구서 정보
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center">
                <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-600">
                  <strong>임차인:</strong> {selectedBilling.tenant_name || '미확인'}
                </span>
              </div>
              <div className="flex items-center">
                <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-600">
                  <strong>전화:</strong> {selectedBilling.tenant_phone || '미확인'}
                </span>
              </div>
              <div className="flex items-center">
                <BanknotesIcon className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-600">
                  <strong>금액:</strong> {selectedBilling.total_amount?.toLocaleString() || '0'}원
                </span>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-600">
                  <strong>청구월:</strong> {selectedBilling.billing_month || '미확인'}
                </span>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              디버그: ID={selectedBilling.id}, 데이터={JSON.stringify(selectedBilling, null, 2)}
            </div>
          </div>
        )}

        {/* SMS 메시지 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SMS 메시지
          </label>
          <textarea
            rows={3}
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="SMS 메시지를 입력하거나 청구서와 은행을 선택하면 자동으로 생성됩니다."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* 전송 버튼 */}
        <div className="mt-4">
          <button
            onClick={sendSms}
            disabled={loading || !selectedBilling || !selectedBank}
            className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                SMS 전송 중...
              </>
            ) : (
              <>
                <PhoneIcon className="h-4 w-4 mr-2" />
                SMS 전송 테스트
              </>
            )}
          </button>
        </div>

        {/* 테스트 결과 */}
        {testResult && (
          <div className="mt-4">
            <div className={`p-4 rounded-md ${
              testResult.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {testResult.success ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  )}
                </div>
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${
                    testResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {testResult.message}
                  </h3>
                  {testResult.error && (
                    <div className={`mt-2 text-sm ${
                      testResult.success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      <p>오류: {testResult.error}</p>
                    </div>
                  )}
                  {testResult.success && testResult.data && (
                    <div className="mt-2 text-sm text-green-700">
                      <pre className="whitespace-pre-wrap text-xs bg-green-100 p-2 rounded">
                        {JSON.stringify(testResult.data, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {testResult.showPaymentButton && testResult.matchResult && (
                    <div className="mt-4">
                      <button
                        onClick={() => processPayment(testResult.matchResult)}
                        disabled={loading}
                        className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            입금처리 중...
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            입금처리 완료하기
                          </>
                        )}
                      </button>
                      
                      <div className="mt-2 text-xs text-green-600">
                        매칭된 청구서: {testResult.matchResult.billing_statement_id}번
                        {testResult.matchResult.property_owner_name && (
                          <span> | 임대사업자: {testResult.matchResult.property_owner_name}</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 빠른 테스트 버튼들 */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            빠른 테스트
          </h4>
          <div className="flex flex-wrap gap-2">
            {banks.map((bank) => (
              <button
                key={bank}
                onClick={() => {
                  setSelectedBank(bank);
                  if (selectedBilling) {
                    const message = generateSmsMessage(bank, selectedBilling);
                    setCustomMessage(message);
                  }
                }}
                disabled={!selectedBilling}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bank}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsTestPanel;
